import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import { styled } from "@mui/material/styles";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import toastError from "../../errors/toastError";
import api from "../../services/api";

const MainPaper = styled(Paper)(({ theme }) => ({
  flex: 1,
  padding: theme.spacing(2),
  margin: theme.spacing(1),
  overflowY: "auto",
  ...theme.scrollbarStyles,
}));

const StatCard = ({ label, value, color }) => (
  <Card variant="outlined" sx={{ height: "100%" }}>
    <CardContent sx={{ textAlign: "center", py: 2 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, color: color || "text.primary" }}>{value}</Typography>
      <Typography variant="body2" color="textSecondary">{label}</Typography>
    </CardContent>
  </Card>
);

const MiniTable = ({ title, rows }) => (
  <>
    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>{title}</Typography>
    <Table size="small" sx={{ mb: 3 }}>
      <TableBody>
        {rows.map((r, i) => (
          <TableRow key={i}>
            <TableCell>{r.name}{r.type ? <Chip size="small" label={r.type} color={r.type === "API oficial" ? "success" : "default"} variant={r.type === "API oficial" ? "filled" : "outlined"} sx={{ ml: 1 }} /> : null}</TableCell>
            <TableCell align="right" sx={{ fontWeight: 600 }}>{r.qtd}</TableCell>
          </TableRow>
        ))}
        {rows.length === 0 && (
          <TableRow><TableCell colSpan={2}><Typography variant="body2" color="textSecondary">Sem dados</Typography></TableCell></TableRow>
        )}
      </TableBody>
    </Table>
  </>
);

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => new Date(Date.now() - n * 86400000).toISOString().slice(0, 10);

const AttendanceDashboard = () => {
  const [startDate, setStartDate] = useState(daysAgo(7));
  const [endDate, setEndDate] = useState(today());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/attendance", { params: { startDate, endDate } });
      setData(data);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const s = data?.summary;

  return (
    <MainContainer>
      <MainHeader>
        <Title>Dashboard de Atendimento</Title>
      </MainHeader>
      <MainPaper variant="outlined">
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center", mb: 2 }}>
          <TextField label="De" type="date" size="small" value={startDate} onChange={(e) => setStartDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Até" type="date" size="small" value={endDate} onChange={(e) => setEndDate(e.target.value)} InputLabelProps={{ shrink: true }} />
          <Button variant="contained" onClick={fetchData} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : "Atualizar"}
          </Button>
        </Box>

        {s && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} md={2}><StatCard label="Tickets" value={s.totalTickets} /></Grid>
            <Grid item xs={6} md={2}><StatCard label="Abertos" value={s.open} color="#1565c0" /></Grid>
            <Grid item xs={6} md={2}><StatCard label="Pendentes" value={s.pending} color="#ed6c02" /></Grid>
            <Grid item xs={6} md={2}><StatCard label="Resolvidos" value={s.closed} color="#2e7d32" /></Grid>
            <Grid item xs={6} md={2}><StatCard label="Recebidas" value={s.messagesReceived} /></Grid>
            <Grid item xs={6} md={2}><StatCard label="Enviadas" value={s.messagesSent} /></Grid>
          </Grid>
        )}

        {data && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}><MiniTable title="Por atendente" rows={data.byUser} /></Grid>
            <Grid item xs={12} md={4}><MiniTable title="Por fila" rows={data.byQueue} /></Grid>
            <Grid item xs={12} md={4}><MiniTable title="Por conexão" rows={data.byConnection} /></Grid>
          </Grid>
        )}
      </MainPaper>
    </MainContainer>
  );
};

export default AttendanceDashboard;
